import { useState } from "react";
import {
  Box,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  ContentCopy as ContentCopyIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EnquiryModal from "../CustomerInteractions/EnquiryModal";
import ShareProperty from "../common/shareProperty";
import SecondaryButton from "../common/SecondaryButton";
import GradientButton from "../common/GradiantButton";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CallIcon from "@mui/icons-material/Call";
import ContactModal from "../contact/ContactModal";
import LoginModal from "../common/LoginModal";

const ProjectDetails = ({ data, selectedPlot, showPopup, setShowPopup }) => {
  const { user_refferal_code, isAuthenticated, isVerified } = useSelector((state) => state.user);
  const location = useLocation();
  const query = location.search.slice(1);
  const [encrypted_property_id, encrypted_user_id] = query.split('/');
  const theme = useSelector((state) => state.themeValue.colors);
  const themeMode = useSelector((state) => state.themeValue.theme);
  const userId = useSelector((state) => state.user.userId);
  const navigate = useNavigate();
  const { slug } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const referralCode = queryParams.get("ref");

  let projectId = null;

  if (slug) {
    const parts = slug.split("-");
    projectId = parts[parts.length - 1];
  }
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [showKycSnackbar, setShowKycSnackbar] = useState(false);

  const handleOpen = () => {
    if (!userId) {
      setShowLoginModal(true);
      return;
    }
    setOpenModal(true);
  }
  const handleModalClose = () => setOpenModal(false);

  const getShareUrl = (property) => {
    const baseUrl = window.location.origin;

    let path = `project/${slug}`;

    if (user_refferal_code) {
      path += `?ref=${user_refferal_code}`;
    }

    return `${baseUrl}/${path}`;
  };


  const plotDetailsArray = selectedPlot
    ? [
      {
        PropertyName: data.project_name,
        Address: data.address,
        City: data.city,
        PlotName: selectedPlot.plotName,
        project_id: data.id,
        plot_id: selectedPlot.id,
        Facing: selectedPlot.Facing,
        SizeInSqFt: selectedPlot.SizeInSqFt,
        SqFtPrice: selectedPlot.SqFtPrice,
        type: data.type,
        AdvancePayment: selectedPlot.AdvancePayment
      },
    ]
    : [];

  // Function to handle contact button click
 const handleContactClick = () => {
  if (!userId) {
    setShowLoginModal(true);
    return;
  }

  if (selectedPlot) {

    const propertyLink = getShareUrl(selectedPlot);

    const totalPrice = selectedPlot.SqFtPrice * selectedPlot.SizeInSqFt;

    const propertyData = {
      PropertyName: data.project_name,
      Address: data.address,
      City: data.city,
      PlotName: selectedPlot.plotName,
      Price: formatIndianPrice(totalPrice),
      PropertyLink: propertyLink
    };

    setSelectedProperty(propertyData);
    setShowContactModal(true);
  }
};
  const formatIndianPrice = (price) => {
    if (!price) return "-";

    const num = Number(price);

    if (num >= 10000000) {
      return `${(num / 10000000).toFixed(2)} Cr`;
    }
    if (num >= 100000) {
      return `${(num / 100000).toFixed(2)} L`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)} K`;
    }
    return `${num}`;
  };

  return (
    <Box sx={{ width: { xs: "100%", sm: "100%", md: "50%" }, p: 1 }}>
      {/* Contact Modal */}
      {showContactModal && selectedProperty && (
        <ContactModal
          open={showContactModal}
          onClose={() => setShowContactModal(false)}
          property={selectedProperty}
        />
      )}

      {/* Project Info */}
      <Box
        sx={{
          bgcolor: theme.background,
          border: `1px solid ${theme.borderColor}`,
          borderRadius: 2,
          mb: 2,
          py: 1.5,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {/* Project Name + Total & Available Plots Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.primary_gradient_start_color,
              fontSize: { xs: "16px", sm: "20px", md: "22px" },
              fontWeight: 600,
            }}
          >
            {data.project_name}
          </Typography>
        </Box>

        {/* Address Row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocationOnIcon sx={{ color: theme.text, fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: theme.text }}>
            {data.address}, {data.city}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginTop: 2 }}>
        {/* Total Plots */}
        <Box sx={{ display: "flex", alignItems: "center", width: "50%", gap: 1 }}>
          <ViewModuleIcon sx={{ color: theme.text, fontSize: 18 }} />
          <Typography variant="body2" sx={{ color: theme.text, fontWeight: 500 }}>
            <strong>Total Plots :</strong> {data?.number_of_plots}
          </Typography>
        </Box>

        {/* Available Plots */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "50%" }}>
          <CheckCircleIcon sx={{ color: theme.text, fontSize: 18 }} />
          <Typography variant="body2" sx={{ color: theme.text, fontWeight: 500 }}>
            <strong>Available Plots :</strong> {data?.available_plots}
          </Typography>
        </Box>
      </Box>

      {/* Plot Info */}
      {selectedPlot ? (
        <Box sx={{ mb: 2, marginTop: 5 }}>
          <Typography variant="h5" sx={{ color: theme.text, mb: 3 }}>
            {selectedPlot.plotName}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              rowGap: 2,
              marginBottom: 4
            }}
          >
            {/* Facing */}
            <Box sx={{ width: "48%", display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: theme.text }}>
                <strong>Facing:</strong> {selectedPlot.Facing}
              </Typography>
            </Box>

            {/* Size */}
            <Box sx={{ width: { xs: "52%", sm: "48%" }, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: theme.text }}>
                <strong>Size:</strong> {selectedPlot.SizeInSqFt} sqft
              </Typography>
            </Box>

            {/* Price */}
            <Box sx={{ width: "48%", display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: theme.text }}>
                <strong>Price:</strong> ₹{formatIndianPrice(selectedPlot.SqFtPrice)}/sqft

              </Typography>
            </Box>

            {/* Advance Payment */}
            <Box
              sx={{
                width: { xs: "52%", sm: "48%" },
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: theme.text, whiteSpace: "nowrap" }}
              >
                <strong>Advance Payment:</strong> ₹{selectedPlot.AdvancePayment}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              // justifyContent: "space-between",
            }}
          >
            {isAuthenticated && (
            <SecondaryButton
              text="Site Visit"
              width={{ xs: "47%", lg: "20%", sm: "47%" }}
              onClick={() => {
                if (!userId) {
                  setShowLoginModal(true);
                  return;
                }
                navigate("/schedulesitevisit", { state: { propertyData: selectedPlot.id } })
              }

              }
            />
            )}

            <SecondaryButton
              text="Enquiry"
              width={{ xs: "47%", lg: "20%", sm: "47%" }}
              onClick={handleOpen}
            />

            <SecondaryButton
              text={
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CallIcon sx={{ mr: 1, fontSize: 25 }} />
                  Contact
                </Box>
              }
              width={{ xs: "47%", lg: "23%", sm: "47%" }}
              onClick={handleContactClick} 
              style={{
                "& svg": {
                  color: "primary.main",
                  transition: "color 0.3s ease",
                },
                "&:hover": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "& svg": {
                    color: "white",
                  },
                },
              }}
            />
            {isAuthenticated && (
            <GradientButton
              text="Pay Advance"
              onClick={() => {
                if (!userId) {
                  setShowLoginModal(true);
                  return;
                }
                if (!isVerified) {
                  alert("Please Verify your account to book the property.");
                  navigate("/myprofile");
                }
                else {
                  navigate("/AdvancePayment", {
                    state: { data: plotDetailsArray, userId },
                  })
                }
                // isKyc !== "Completed"
                //   ? setShowKycSnackbar(true)
                //   : navigate("/AdvancePayment", {
                //     state: { data: plotDetailsArray, userId },
                //   })
              }
              }
              sx={{ width: { xs: "47%", lg: "28%", sm: "47%" } }}
            />
            )}
          </Box>

          {openModal && (
            <EnquiryModal open={openModal} onClose={handleModalClose} propertyData={selectedPlot.id} />
          )}

          {showPopup && selectedPlot && (
            <ShareProperty property={selectedPlot} getShareUrl={getShareUrl} onClose={() => setShowPopup(false)} />
          )}
        </Box>
      ) : (
        <Typography sx={{ textAlign: "center", color: "#888", p: 2 }}>
          Select a plot to view details
        </Typography>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showKycSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowKycSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message="Please complete your KYC to book the property."
        action={
          <GradientButton
            text="Update KYC"
            onClick={() => {
              setShowKycSnackbar(false);
              navigate("/KYC");
            }}
          />
        }
      />
      {showLoginModal && (
        <LoginModal onClose={() => setShowLoginModal(false)} />
      )}
    </Box>
  );
};

export default ProjectDetails;